import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { getSessionId } from "../../lib/analytics";

export default function NewsletterSignup({
    variant = "light",
    title = "Receive our free travel guide.",
    overline = "The slow letter",
    body = "One short, considered letter a season — plus instant access to our interactive flipbook guide of every destination, experience and journey. No noise. Unsubscribe in a single click.",
}) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const isDark = variant === "dark";

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Please tell us your name.");
            return;
        }
        if (phone.replace(/\D/g, "").length < 10) {
            toast.error("Please enter a valid mobile number.");
            return;
        }
        if (!email.trim() || !email.includes("@")) {
            toast.error("Please enter a valid email address.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/newsletter/subscribe", {
                email: email.trim(),
                name: name.trim(),
                phone: phone.trim(),
                consent_marketing: true,
                source_path: typeof window !== "undefined" ? window.location.pathname : "",
                session_id: getSessionId() || "",
            });
            setDone(true);
            toast.success("Thank you — your flipbook guide link is on its way to your inbox.");
            setEmail("");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err?.response?.data?.detail) ||
                    "Could not subscribe. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            className={`section-padding ${isDark ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}
            data-testid="newsletter-signup"
        >
            <div className="container-page grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                <div className="lg:col-span-6">
                    <p className={`overline mb-4 ${isDark ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                        {overline}
                    </p>
                    <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05] max-w-xl">
                        {title}
                    </h2>
                    <p className={`mt-6 text-base leading-relaxed max-w-lg ${isDark ? "text-primary-foreground/80" : "text-foreground/80"}`}>
                        {body}
                    </p>
                </div>
                <form onSubmit={onSubmit} className="lg:col-span-6 space-y-5" data-testid="newsletter-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <label className="block">
                            <span className={`overline mb-3 block ${isDark ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                                Your name
                            </span>
                            <input
                                required
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Asha Traveller"
                                data-testid="newsletter-name"
                                className={`w-full bg-transparent border-b py-3 text-lg focus:outline-none transition-colors ${
                                    isDark
                                        ? "border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/40 focus:border-accent"
                                        : "border-foreground/20 text-foreground placeholder:text-muted-foreground focus:border-primary"
                                }`}
                            />
                        </label>
                        <label className="block">
                            <span className={`overline mb-3 block ${isDark ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                                Mobile number
                            </span>
                            <input
                                required
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+91 98XXX XXXXX"
                                data-testid="newsletter-phone"
                                className={`w-full bg-transparent border-b py-3 text-lg focus:outline-none transition-colors ${
                                    isDark
                                        ? "border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/40 focus:border-accent"
                                        : "border-foreground/20 text-foreground placeholder:text-muted-foreground focus:border-primary"
                                }`}
                            />
                        </label>
                    </div>
                    <label className="block">
                        <span className={`overline mb-3 block ${isDark ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                            Email address
                        </span>
                        <input
                            required
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@yourdomain.com"
                            data-testid="newsletter-email"
                            className={`w-full bg-transparent border-b py-3 text-lg focus:outline-none transition-colors ${
                                isDark
                                    ? "border-primary-foreground/30 text-primary-foreground placeholder:text-primary-foreground/40 focus:border-accent"
                                    : "border-foreground/20 text-foreground placeholder:text-muted-foreground focus:border-primary"
                            }`}
                        />
                    </label>
                    <div className="flex flex-wrap items-center gap-4">
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="newsletter-submit"
                            className={`inline-flex items-center justify-center gap-3 px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold disabled:opacity-60 transition-colors ${
                                isDark
                                    ? "bg-accent text-accent-foreground hover:bg-accent/85"
                                    : "bg-primary text-primary-foreground hover:bg-accent"
                            }`}
                        >
                            {submitting ? "Subscribing…" : done ? "Subscribed ✓" : "Get the guide"}
                            <ArrowRight size={14} strokeWidth={1.5} />
                        </button>
                        {done && (
                            <a
                                href="/guide"
                                data-testid="newsletter-download"
                                className={`inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-semibold underline-offset-4 hover:underline ${
                                    isDark ? "text-accent" : "text-primary"
                                }`}
                            >
                                <BookOpen size={14} strokeWidth={1.5} />
                                Open the flipbook guide
                            </a>
                        )}
                    </div>
                    <p className={`text-[11px] tracking-wide ${isDark ? "text-primary-foreground/50" : "text-muted-foreground"}`}>
                        By subscribing you consent to the use of your details by Whispering Earth LLP, its subsidiaries
                        and partners for marketing and service purposes. No re-sale, no spam — unsubscribe in one click.
                    </p>
                </form>
            </div>
        </section>
    );
}
